import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-manrope font-bold text-xl text-slate-900 mb-3">{title}</h2>
    <div className="text-sm text-slate-800 leading-relaxed space-y-3">{children}</div>
  </section>
);

const TermsOfUsePage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="tricolor-bar" />
      <div className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/" className="text-white/60 hover:text-white text-xs font-bold uppercase tracking-widest">← Back to Home</Link>
          <h1 className="font-manrope font-bold text-3xl md:text-4xl mt-4">Terms of Use</h1>
          <p className="text-white/60 text-sm mt-3">weightloss-assessment.in · Last updated: 3 July 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Section title="1. Acceptance of Terms">
          <p>
            By accessing or using weightloss-assessment.in ("the Website"), including the obesity
            risk assessment and Digital Twin simulation tools, you agree to be bound by these Terms
            of Use and our{' '}
            <Link to="/privacy-policy" className="text-indigo-600 font-semibold underline">Privacy Policy</Link>.
            If you do not agree, please do not use the Website.
          </p>
        </Section>

        <Section title="2. Educational Purpose Only">
          <p>
            The Digital Twin and obesity assessment are predictive educational tools based on the
            information you provide and published medical literature. The outputs are estimates
            only and should not be considered a diagnosis, treatment recommendation, or a
            substitute for consultation with a qualified healthcare professional.
          </p>
          <p>
            All projections — including weight trajectories, disease remission probabilities,
            surgical risk estimates, and financial figures — are statistical estimates derived from
            population-level evidence. Individual results vary, and no specific outcome is promised
            or guaranteed.
          </p>
        </Section>

        <Section title="3. No Doctor-Patient Relationship">
          <p>
            Use of this Website does not establish a doctor-patient relationship between the user
            and Dr. Prem Kumar, Lumen Metabolic, or any associated healthcare providers. A
            doctor-patient relationship is formed only through a formal, in-person or duly
            conducted clinical consultation.
          </p>
        </Section>

        <Section title="4. Not for Emergencies">
          <p>
            The Website is not intended for use in medical emergencies. If you are experiencing a
            medical emergency, contact your local emergency services or go to the nearest hospital
            immediately.
          </p>
        </Section>

        <Section title="5. AI-Assisted Tools">
          <p>
            AI-assisted tools may be used to generate personalised simulations and educational
            insights. Outputs are generated automatically from the information you provide and
            should always be reviewed with a qualified healthcare professional before making any
            health decision.
          </p>
        </Section>

        <Section title="6. No Guarantee of Outcomes">
          <p>
            Nothing on this Website constitutes a promise or guarantee of any health outcome,
            including weight loss, disease remission, symptom improvement, or cost savings. All
            figures represent projected estimates based on available clinical evidence.
          </p>
        </Section>

        <Section title="7. User Responsibilities">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Provide accurate information when using the assessment tools.</li>
            <li>Use the Website only for your own personal, non-commercial purposes.</li>
            <li>Do not attempt to interfere with, disrupt, or gain unauthorised access to the Website or its data.</li>
            <li>You must be at least 18 years of age to use the assessment tools.</li>
          </ul>
        </Section>

        <Section title="8. Intellectual Property">
          <p>
            The Website, the MIS™ Digital Twin system, the Meta-X platform, and all associated
            software, simulation models, content, design, and branding are the intellectual
            property of their respective owners and are protected by applicable intellectual
            property laws. You may not copy, reproduce, modify, distribute, reverse-engineer, or
            create derivative works from any part of the Website or its underlying systems without
            prior written permission.
          </p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, the Website, its owners, operators, and
            associated healthcare providers shall not be liable for any direct, indirect,
            incidental, consequential, or special damages arising from your use of, or inability to
            use, the Website or its outputs — including any decision made or action taken in
            reliance on any assessment, simulation, or projection provided by the Website.
          </p>
        </Section>

        <Section title="10. Governing Law">
          <p>
            These Terms of Use are governed by the laws of India. Any disputes arising from the use
            of this Website shall be subject to the exclusive jurisdiction of the competent courts
            in India.
          </p>
        </Section>

        <Section title="11. Changes to These Terms">
          <p>
            We may revise these Terms of Use from time to time. The "Last updated" date at the top
            of this page reflects the most recent revision. Continued use of the Website after
            changes are posted constitutes acceptance of the revised terms.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Questions about these terms may be sent to{' '}
            <a href="mailto:contact@weightloss-assessment.in" className="text-indigo-600 font-semibold underline">
              contact@weightloss-assessment.in
            </a>
          </p>
        </Section>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfUsePage;
